import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { DownloadIcon, InfoIcon } from '@common/utils/icons/icons';
import Mission from '../../models/missions/mission';
import SystemInformationDialog from '../robots/SystemInformationDialog';
import Button from '@mui/material/Button';
import MissionRuns from './MissionRuns';
import MissionDetails from './MissionDetails';
import MissionMap from './MissionMap';
import { useAppSelector } from '../../store';
import { selectUser } from '../../store/general/general-slice-selectors';
import OrganizationRole from '../../models/enums/organization-roles';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { convertReportParametersToQuery } from '../../models/general/notifications/report-parameters';
import { NotificationTrigger } from '../../models/enums/notifications';
import { FilterKeys } from '@common/enums/query-params/filters';
import { useSearchParams } from 'react-router-dom';

const MissionInfo: React.FC<{
  mission: Mission | null;
}> = ({ mission }) => {
  const { t } = useTranslation();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setFilterParams] = useSearchParams();

  const [isSystemInformationDialogOpen, setIsSystemInformationDialogOpen] = useState(false);

  const userData = useAppSelector((state) => selectUser(state));

  if (mission == null) {
    return null;
  }

  const openSystemInformationDialogClickHandler = () => {
    setIsSystemInformationDialogOpen(true);
  };

  const closeSystemInformationDialogHandler = () => {
    setIsSystemInformationDialogOpen(false);
  };

  const downloadMissionClickHandler = () => {
    setFilterParams((prevState) => {
      prevState.set(
        FilterKeys.Report,
        convertReportParametersToQuery({
          trigger: NotificationTrigger.MISSION_SUMMARY,
          robotId: mission.deployment.robotId,
          startTime: mission.startTime,
          endTime: mission.endTime,
        }),
      );

      return prevState;
    });
  };

  return (
    <React.Fragment>
      <Stack
        sx={(theme) => ({
          p: 2,
          backgroundColor: theme.palette.background.default,
        })}
        spacing={2}
      >
        {userData.data.organization.role !== OrganizationRole.LOCATION &&
          userData.data.organization.role !== OrganizationRole.CUSTOMER && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <TooltipIconButton
                tooltip={t('info-tooltip-download-report')}
                onClick={downloadMissionClickHandler}
                icon={<DownloadIcon />}
                color="primary"
              />
              <Button
                onClick={openSystemInformationDialogClickHandler}
                color="primary"
                size="large"
                startIcon={<InfoIcon data-testid="button-info" />}
              >
                {t('info-tooltip-robot-system-information')}
              </Button>
            </Stack>
          )}
        <Grid container>
          <MissionMap mission={mission} />
          <Grid item xs={12} md={6}>
            <MissionDetails mission={mission} />
          </Grid>
        </Grid>
        <MissionRuns mission={mission} />
      </Stack>
      <SystemInformationDialog
        serialNumber={mission.deployment.robotSerialNumber}
        softwareVersion={mission.softwareVersion}
        hardwareVersion={mission.hardwareVersion}
        open={isSystemInformationDialogOpen}
        onClose={closeSystemInformationDialogHandler}
      />
    </React.Fragment>
  );
};

export default MissionInfo;
